import React, { useEffect, useRef, useState, useMemo } from "react";
import { Collapse } from "antd";
import { SimpleLoader } from "./sub/Loaders";
import TopPlanElement from "./TopPlanElement";

const { Panel } = Collapse;

function GroupedPlans(props) {
  const BottomRef = useRef(null);
  const [activeKeys, setActiveKeys] = useState([]);

  // Group plans by planGroup
  const groupedPlans = useMemo(() => {
    if (!props.data?.length) return {};
    
    const groups = {};
    
    props.data.forEach((plan) => {
      const planGroupKey = plan.planGroup 
        ? `plangroup-${plan.planGroup}` 
        : 'no-plangroup';
      
      if (!groups[planGroupKey]) {
        groups[planGroupKey] = {
          planGroup: plan.planGroup || null,
          plans: []
        };
      }
      
      groups[planGroupKey].plans.push(plan);
    });
    
    return groups;
  }, [props.data]);

  // Initialize active keys to show all plan groups by default
  useEffect(() => {
    const keys = Object.keys(groupedPlans);
    setActiveKeys(keys);
  }, [groupedPlans]);

  useEffect(() => {
    if (props.data?.length > 0) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !props.loading &&
            props.totalTopPlanCount > props.data.length
          ) {
            props.setStartAt(props.startAt + 20);
            props.loadMore(props.startAt + 20);
          }
        });
      });
      observer.observe(BottomRef.current);
      return () => observer.disconnect();
    }
  }, [props]);

  const renderPlanGroup = (planGroupKey, group) => {
    const planGroupName = group.planGroup || "No Plan Group Assigned";
    const planCount = group.plans.length;
    
    // Calculate cumulative progress for this group
    const calculateGroupProgress = () => {
      // First check if we have progress data from the API
      if (props.planGroupProgress && props.planGroupProgress[planGroupName] !== undefined) {
        return props.planGroupProgress[planGroupName];
      }
      
      // Fallback to client-side calculation if API data is not available
      let totalProgress = 0;
      let validPlansCount = 0;
      
      group.plans.forEach(plan => {
        const planProgress = plan.target > 0 
          ? ((plan.achievedTarget || 0) / plan.target) * 100 
          : 0;
        
        if (plan.target > 0) {
          totalProgress += planProgress;
          validPlansCount++;
        }
      });
      
      return validPlansCount > 0 ? parseFloat(((totalProgress / validPlansCount)).toFixed(2)) : 0;
    };
    
    const groupProgress = calculateGroupProgress();
    
    // Get color based on progress percentage
    const getProgressColor = (progress) => {
      if (progress < 30) return '#ef4444'; // red
      if (progress >= 30 && progress <= 60) return '#eab308'; // yellow
      return '#22c55e'; // green
    };

    // Get washed background color for the cylindrical badge
    const getProgressBackgroundColor = (progress) => {
      if (progress < 30) return 'rgba(239, 68, 68, 0.1)'; // washed red
      if (progress >= 30 && progress <= 60) return 'rgba(234, 179, 8, 0.1)'; // washed yellow
      return 'rgba(34, 197, 94, 0.1)'; // washed green
    };
    
    return (
      <Panel
        header={
          <div className="flex justify-between items-center w-full py-1">
            <div className="flex items-center gap-4">
              <div className="font-bold text-lg text-gray-800">
                {planGroupName}
              </div>
              <div 
                className="px-3 py-1 text-xs font-semibold rounded-full"
                style={{ 
                  color: getProgressColor(groupProgress),
                  backgroundColor: getProgressBackgroundColor(groupProgress),
                  border: `1px solid ${getProgressColor(groupProgress)}20`
                }}
              >
                {groupProgress.toFixed(2)}%
              </div>
            </div>

          </div>
        }
        key={planGroupKey}
      >
        <div className="space-y-4">
          {group.plans.map((plan, index) => (
            <TopPlanElement
              key={plan.planId}
              plans={group.plans}
              data={plan}
              handleDelete={props.handleDelete}
              handleEdit={props.handleEdit}
            />
          ))}
        </div>
      </Panel>
    );
  };

  if (!Object.keys(groupedPlans).length && !props.loading) {
    return <div className="text-center text-gray-500 py-8">No plans available</div>;
  }

  return (
    <>
      <Collapse
        activeKey={activeKeys}
        onChange={setActiveKeys}
        bordered={false}
        className="grouped-plans"
      >
        {Object.entries(groupedPlans).map(([planGroupKey, group]) =>
          renderPlanGroup(planGroupKey, group)
        )}
      </Collapse>
      
      {/* Intersection observer target for infinite scroll */}
      <div ref={BottomRef} />
      
      {props.loading && <SimpleLoader />}
    </>
  );
}

export default GroupedPlans;
